/**
 * API Hooks for Admin Console
 * 
 * Custom hooks for data fetching using React Query patterns
 * (Simplified version without react-query dependency)
 */

import { useState, useEffect, useCallback } from 'react';
import api from '../api/client';

/**
 * Generic fetch hook with loading/error states
 */
export function useFetch(endpoint, options = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const { enabled = true, refreshInterval = null } = options;
  
  const refetch = useCallback(async () => {
    if (!enabled || !endpoint) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const result = await api.get(endpoint);
      setData(result);
    } catch (err) {
      // Ensure error is always a string
      const errorMessage = err?.message || (typeof err === 'string' ? err : 'An error occurred');
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [endpoint, enabled]);
  
  useEffect(() => {
    refetch();
    
    if (refreshInterval) {
      const interval = setInterval(refetch, refreshInterval);
      return () => clearInterval(interval);
    }
  }, [refetch, refreshInterval]);
  
  return { data, loading, error, refetch };
}

/**
 * Dashboard stats hook
 */
export function useDashboardStats() {
  return useFetch('/api/dashboard/stats', { refreshInterval: 180000 });
}

/**
 * Clients list hook
 */
export function useClients(options = {}) {
  const { status, limit = 50, offset = 0 } = options;
  let endpoint = `/api/clients?limit=${limit}&offset=${offset}`;
  if (status) endpoint += `&status=${status}`;
  return useFetch(endpoint);
}

/**
 * Campaigns list hook
 */
export function useCampaigns(options = {}) {
  const { clientId, status, limit = 50, offset = 0 } = options;
  let endpoint = `/api/campaigns?limit=${limit}&offset=${offset}`;
  if (clientId) endpoint += `&client_id=${clientId}`;
  if (status) endpoint += `&status=${status}`;
  return useFetch(endpoint);
}

/**
 * Single campaign hook
 * Fetches campaign details by ID
 */
export function useCampaign(campaignId) {
  return useFetch(campaignId ? `/api/campaigns/${campaignId}` : null);
}



/**
 * Errors/Failed jobs hook - supports severity and category filtering
 */
export function useErrors(options = {}) {
  const { status = 'pending', severity, category, clientId, campaignId, limit = 50 } = options;
  let endpoint = `/api/errors?status=${status}&limit=${limit}`;
  if (severity) endpoint += `&severity=${severity}`;
  if (category) endpoint += `&category=${category}`;
  if (clientId) endpoint += `&client_id=${clientId}`;
  if (campaignId) endpoint += `&campaign_id=${campaignId}`;
  return useFetch(endpoint, { refreshInterval: 10000 });
}

/**
 * Error categories with counts
 */
export function useErrorCategories() {
  return useFetch('/api/errors/categories');
}

/**
 * Queues stats hook
 * Auto-refresh managed by component (60s default)
 */
export function useQueues() {
  return useFetch('/api/queues');
}

/**
 * Kill switch status hook
 * No auto-refresh - manual refresh only for safety
 */
export function useKillSwitch() {
  return useFetch('/api/kill-switch/status');
}

/**
 * Templates list hook
 */
export function useTemplates() {
  return useFetch('/api/templates/list');
}

/**
 * Onboarding sessions hook
 */
export function useOnboardingSessions() {
  return useFetch('/api/onboarding/sessions');
}

/**
 * Instantly campaigns hook
 */
export function useInstantlyCampaigns() {
  return useFetch('/api/instantly/campaigns');
}

/**
 * Mutation hook for POST/PUT/DELETE operations
 */
export function useMutation(endpoint, method = 'POST') {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const mutate = useCallback(async (body) => {
    setLoading(true);
    setError(null);
    
    try {
      let result;
      if (method === 'POST') {
        result = await api.post(endpoint, body);
      } else if (method === 'PUT') {
        result = await api.put(endpoint, body);
      } else if (method === 'DELETE') {
        result = await api.delete(endpoint);
      }
      return result;
    } catch (err) {
      const errorMessage = err?.message || (typeof err === 'string' ? err : 'An error occurred');
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [endpoint, method]);
  
  return { mutate, loading, error };
}

/**
 * Quick add client mutation
 */
export function useQuickAddClient() {
  return useMutation('/api/clients/quick-add');
}

/**
 * Retry job mutation
 */
export function useRetryJob() {
  return useMutation('/api/retry-job');
}

/**
 * Resolve job mutation
 */
export function useResolveJob() {
  return useMutation('/api/resolve-job');
}

/**
 * Retry all pending jobs mutation
 */
export function useRetryAllPending() {
  return useMutation('/api/retry-all-pending');
}

/**
 * Bulk resolve errors by category
 */
export function useBulkResolveByCategory() {
  return useMutation('/api/errors/bulk-resolve-category');
}

/**
 * Kill switch actions
 */
export function useKillSwitchKill() {
  return useMutation('/api/kill-switch/kill');
}

export function useKillSwitchResume() {
  return useMutation('/api/kill-switch/resume');
}

/**
 * Toggle feature mutation
 */
export function useToggleFeature() {
  return useMutation('/api/toggle-feature');
}

/**
 * Create client from onboarding
 */
export function useCreateClientFromOnboarding() {
  return useMutation('/api/onboarding/create-client');
}

/**
 * Resend onboarding link email
 */
export function useResendOnboardingLink() {
  return useMutation('/api/onboarding/resend-link');
}

/**
 * Save template
 */
export function useSaveTemplate() {
  return useMutation('/api/templates/save');
}

/**
 * Template detail hook
 */
export function useTemplateDetail(industry) {
  return useFetch(`/api/templates/get?industry=${industry}`, { enabled: !!industry });
}

/**
 * Template AI Preview - generate real AI icebreakers
 */
export function useTemplateAIPreview() {
  return useMutation('/api/templates/ai-preview');
}

/**
 * Template AI Improve - improve email copy with AI
 */
export function useTemplateAIImprove() {
  return useMutation('/api/templates/ai-improve');
}

/**
 * Template cost stats - get real AI usage costs
 */
export function useTemplateCostStats(days = 30) {
  return useFetch(`/api/templates/cost-stats?days=${days}`);
}

/**
 * Template sample leads - get real leads for AI preview
 */
export function useTemplateSampleLeads(industry) {
  return useFetch(`/api/templates/sample-leads${industry ? `?industry=${industry}` : ''}`);
}

/**
 * Leads list hook - supports CRM filters
 */
export function useLeads(options = {}) {
  const { 
    search = '', 
    status = '', 
    clientId = '', 
    temperature = '',
    lifecycle = '',
    limit = 50, 
    offset = 0, 
    enabled = true 
  } = options;
  let endpoint = `/api/leads?limit=${limit}&offset=${offset}`;
  if (search) endpoint += `&search=${encodeURIComponent(search)}`;
  if (status) endpoint += `&status=${status}`;
  if (clientId) endpoint += `&client_id=${clientId}`;
  if (temperature) endpoint += `&temperature=${temperature}`;
  if (lifecycle) endpoint += `&lifecycle=${lifecycle}`;
  return useFetch(endpoint, { enabled });
}

/**
 * Lead detail hook
 */
export function useLeadDetail(leadId) {
  return useFetch(`/api/leads/${leadId}`, { enabled: !!leadId });
}

/**
 * Requeue lead mutation
 */
export function useRequeueLead() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const mutate = async (leadId) => {
    setLoading(true);
    setError(null);
    try {
      const result = await api.post(`/api/leads/${leadId}/requeue`);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };
  
  return { mutate, loading, error };
}

/**
 * Update lead mutation
 */
export function useUpdateLead() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const mutate = async (leadId, updates) => {
    setLoading(true);
    setError(null);
    try {
      const result = await api.patch(`/api/leads/${leadId}`, updates);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };
  
  return { mutate, loading, error };
}

/**
 * Support tickets list hook
 */
export function useSupportTickets(options = {}) {
  const { status = '', priority = '', limit = 50, offset = 0 } = options;
  let endpoint = `/api/support-tickets?limit=${limit}&offset=${offset}`;
  if (status) endpoint += `&status=${status}`;
  if (priority) endpoint += `&priority=${priority}`;
  return useFetch(endpoint, { refreshInterval: 30000 });
}

/**
 * Support ticket detail hook
 */
export function useSupportTicketDetail(ticketId) {
  return useFetch(`/api/support-tickets/${ticketId}`, { enabled: !!ticketId });
}

/**
 * Respond to ticket mutation
 */
export function useRespondToTicket() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const mutate = async (ticketId, message, respondedBy = 'Admin') => {
    setLoading(true);
    setError(null);
    try {
      const result = await api.post(`/api/support-tickets/${ticketId}/respond`, { message, respondedBy });
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };
  
  return { mutate, loading, error };
}

/**
 * Update ticket status mutation
 */
export function useUpdateTicketStatus() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const mutate = async (ticketId, updates) => {
    setLoading(true);
    setError(null);
    try {
      const result = await api.patch(`/api/support-tickets/${ticketId}`, updates);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };
  
  return { mutate, loading, error };
}

/**
 * Activity logs hook
 */
export function useActivity(options = {}) {
  const { limit = 50, offset = 0, type = '' } = options;
  let endpoint = `/api/activity?limit=${limit}&offset=${offset}`;
  if (type) endpoint += `&type=${type}`;
  return useFetch(endpoint, { refreshInterval: 30000 });
}

/**
 * API costs hook
 */
export function useCosts(options = {}) {
  const { days = 30 } = options;
  return useFetch(`/api/costs?days=${days}`);
}

/**
 * Feature flags hook
 */
export function useFeatureFlags() {
  return useFetch('/api/feature-flags');
}

/**
 * Set feature flag mutation
 */
export function useSetFeatureFlag() {
  return useMutation('/api/feature-flags');
}

/**
 * Analytics overview hook
 */
export function useAnalyticsOverview(options = {}) {
  const { days = 30 } = options;
  return useFetch(`/api/analytics/overview?days=${days}`);
}

/**
 * Analytics campaigns hook
 */
export function useAnalyticsCampaigns(options = {}) {
  const { limit = 20 } = options;
  return useFetch(`/api/analytics/campaigns?limit=${limit}`);
}

/**
 * Notification preferences hook
 */
export function useNotificationPreferences() {
  return useFetch('/api/notification-preferences');
}

/**
 * Set notification preference mutation
 */
export function useSetNotificationPreference() {
  return useMutation('/api/notification-preferences');
}

/**
 * Quarantine hook
 */
export function useQuarantine(options = {}) {
  const { resolved = false, limit = 50 } = options;
  return useFetch(`/api/quarantine?resolved=${resolved}&limit=${limit}`);
}

/**
 * Resolve quarantined webhook mutation
 */
export function useResolveQuarantined() {
  return useMutation('/api/quarantine/resolve');
}

/**
 * Bulk resolve quarantined webhooks mutation
 */
export function useBulkResolveQuarantined() {
  return useMutation('/api/quarantine/bulk-resolve');
}

// ============================================================================
// OBSERVABILITY HOOKS (Axiom)
// ============================================================================

/**
 * Observability overview hook - total requests, errors, warnings
 */
export function useObservabilityOverview(range = '1h') {
  return useFetch(`/api/observability/overview?range=${range}`);
}

/**
 * Observability errors hook - error details
 */
export function useObservabilityErrors(range = '1h') {
  return useFetch(`/api/observability/errors?range=${range}`);
}

/**
 * Observability API health hook - external API stats
 */
export function useObservabilityApiHealth(range = '1h') {
  return useFetch(`/api/observability/api-health?range=${range}`);
}

/**
 * Observability workers hook - per-worker stats
 */
export function useObservabilityWorkers(range = '1h') {
  return useFetch(`/api/observability/workers?range=${range}`);
}

/**
 * Observability timeseries hook - request/error trends
 */
export function useObservabilityTimeseries(range = '1h') {
  return useFetch(`/api/observability/timeseries?range=${range}`);
}

// ============================================================================
// CLIENT DETAIL HOOKS (CRM)
// ============================================================================

/**
 * Single client detail hook
 */
export function useClientDetail(clientId) {
  return useFetch(clientId ? `/api/clients/${clientId}` : null);
}

/**
 * Client leads hook
 */
export function useClientLeads(clientId, options = {}) {
  const { limit = 100 } = options;
  return useFetch(clientId ? `/api/clients/${clientId}/leads?limit=${limit}` : null);
}

/**
 * Client campaigns hook
 */
export function useClientCampaigns(clientId) {
  return useFetch(clientId ? `/api/clients/${clientId}/campaigns` : null);
}

/**
 * Client stats hook (aggregated performance metrics)
 */
export function useClientStats(clientId) {
  return useFetch(clientId ? `/api/clients/${clientId}/stats` : null);
}

/**
 * Client billing hook (Stripe subscription & invoices)
 */
export function useClientBilling(clientId) {
  return useFetch(clientId ? `/api/clients/${clientId}/billing` : null);
}

/**
 * Update client mutation hook
 */
export function useUpdateClient(clientId) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const mutate = useCallback(async (data) => {
    if (!clientId) return null;
    
    setLoading(true);
    setError(null);
    
    try {
      const result = await api.patch(`/api/clients/${clientId}`, data);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [clientId]);
  
  return { mutate, loading, error };
}

/**
 * Client activity (lead events timeline) hook
 */
export function useClientActivity(clientId, options = {}) {
  const { limit = 50, type = null } = options;
  let url = clientId ? `/api/clients/${clientId}/activity?limit=${limit}` : null;
  if (url && type) {
    url += `&type=${type}`;
  }
  return useFetch(url);
}

/**
 * Client notes hook
 */
export function useClientNotes(clientId) {
  return useFetch(clientId ? `/api/clients/${clientId}/notes` : null);
}

/**
 * Create client note mutation hook
 */
export function useCreateNote(clientId) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const mutate = useCallback(async (data) => {
    if (!clientId) return null;
    
    setLoading(true);
    setError(null);
    
    try {
      const result = await api.post(`/api/clients/${clientId}/notes`, data);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [clientId]);
  
  return { mutate, loading, error };
}

/**
 * Delete client note mutation hook
 */
export function useDeleteNote(clientId) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const mutate = useCallback(async (noteId) => {
    if (!clientId || !noteId) return null;
    
    setLoading(true);
    setError(null);
    
    try {
      const result = await api.delete(`/api/clients/${clientId}/notes/${noteId}`);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [clientId]);
  
  return { mutate, loading, error };
}

// ============================================================================
// PROSPECTS (Sales Pipeline)
// ============================================================================

/**
 * Prospects list hook
 */
export function useProspects(options = {}) {
  const { stage, owner, search, limit = 50, offset = 0 } = options;
  let url = `/api/prospects?limit=${limit}&offset=${offset}`;
  if (stage) url += `&stage=${stage}`;
  if (owner) url += `&owner=${encodeURIComponent(owner)}`;
  if (search) url += `&search=${encodeURIComponent(search)}`;
  return useFetch(url);
}

/**
 * Prospect stats hook
 */
export function useProspectStats() {
  return useFetch('/api/prospects/stats');
}

/**
 * Prospect detail hook
 */
export function useProspectDetail(prospectId) {
  return useFetch(prospectId ? `/api/prospects/${prospectId}` : null);
}

/**
 * Create prospect mutation hook
 */
export function useCreateProspect() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const mutate = useCallback(async (data) => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await api.post('/api/prospects', data);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);
  
  return { mutate, loading, error };
}

/**
 * Update prospect mutation hook
 */
export function useUpdateProspect() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const mutate = useCallback(async (prospectId, data) => {
    if (!prospectId) return null;
    
    setLoading(true);
    setError(null);
    
    try {
      const result = await api.patch(`/api/prospects/${prospectId}`, data);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);
  
  return { mutate, loading, error };
}

/**
 * Delete prospect mutation hook
 */
export function useDeleteProspect() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const mutate = useCallback(async (prospectId) => {
    if (!prospectId) return null;
    
    setLoading(true);
    setError(null);
    
    try {
      const result = await api.delete(`/api/prospects/${prospectId}`);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);
  
  return { mutate, loading, error };
}

/**
 * Create prospect activity mutation hook
 */
export function useCreateProspectActivity() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const mutate = useCallback(async (prospectId, data) => {
    if (!prospectId) return null;
    
    setLoading(true);
    setError(null);
    
    try {
      const result = await api.post(`/api/prospects/${prospectId}/activities`, data);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);
  
  return { mutate, loading, error };
}

/**
 * Convert prospect to client mutation hook
 */
export function useConvertProspect() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const mutate = useCallback(async (prospectId, data) => {
    if (!prospectId) return null;
    
    setLoading(true);
    setError(null);
    
    try {
      const result = await api.post(`/api/prospects/${prospectId}/convert`, data);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);
  
  return { mutate, loading, error };
}

// ============================================================================
// ALERTS
// ============================================================================

/**
 * Alerts list hook
 */
export function useAlerts(options = {}) {
  const { type, severity, resolved = 'false', limit = 50 } = options;
  let url = `/api/alerts?resolved=${resolved}&limit=${limit}`;
  if (type) url += `&type=${type}`;
  if (severity) url += `&severity=${severity}`;
  return useFetch(url, { refreshInterval: 30000 });
}

/**
 * Alert stats hook
 */
export function useAlertStats() {
  return useFetch('/api/alerts/stats', { refreshInterval: 30000 });
}

/**
 * Resolve alert mutation hook
 */
export function useResolveAlert() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const mutate = useCallback(async (alertId, data = {}) => {
    if (!alertId) return null;
    
    setLoading(true);
    setError(null);
    
    try {
      const result = await api.post(`/api/alerts/${alertId}/resolve`, data);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);
  
  return { mutate, loading, error };
}

/**
 * Snooze alert mutation hook
 */
export function useSnoozeAlert() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const mutate = useCallback(async (alertId, hours = 24) => {
    if (!alertId) return null;
    
    setLoading(true);
    setError(null);
    
    try {
      const result = await api.post(`/api/alerts/${alertId}/snooze`, { hours });
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);
  
  return { mutate, loading, error };
}

// ============================================================================
// CAMPAIGN MUTATION HOOKS
// ============================================================================

/**
 * Create campaign mutation hook
 */
export function useCreateCampaign() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const mutate = useCallback(async (data) => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await api.post('/api/campaigns', data);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);
  
  return { mutate, loading, error };
}

/**
 * Update campaign mutation hook (pause, resume, rename, etc.)
 */
export function useUpdateCampaign() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const mutate = useCallback(async (campaignId, data) => {
    if (!campaignId) return null;
    
    setLoading(true);
    setError(null);
    
    try {
      const result = await api.patch(`/api/campaigns/${campaignId}`, data);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);
  
  return { mutate, loading, error };
}

/**
 * Delete campaign mutation hook
 */
export function useDeleteCampaign() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const mutate = useCallback(async (campaignId) => {
    if (!campaignId) return null;
    
    setLoading(true);
    setError(null);
    
    try {
      const result = await api.delete(`/api/campaigns/${campaignId}`);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);
  
  return { mutate, loading, error };
}

/**
 * Duplicate campaign mutation hook
 */
export function useDuplicateCampaign() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const mutate = useCallback(async (campaignId, options = {}) => {
    if (!campaignId) return null;
    
    setLoading(true);
    setError(null);
    
    try {
      const result = await api.post(`/api/campaigns/${campaignId}/duplicate`, options);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);
  
  return { mutate, loading, error };
}

/**
 * Upload campaign CSV mutation hook (creates NEW campaign from CSV)
 */
export function useUploadCampaignCSV() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const mutate = useCallback(async (formData) => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await api.postForm('/api/campaigns/upload', formData);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);
  
  return { mutate, loading, error };
}

/**
 * Import leads into EXISTING campaign via CSV
 * Uses POST /api/campaigns/:id/leads
 * This appends leads to the campaign's D1 table and updates total_leads count
 */
export function useImportCampaignLeads() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const mutate = useCallback(async (campaignId, formData) => {
    if (!campaignId) throw new Error('Campaign ID is required');
    
    setLoading(true);
    setError(null);
      
    try {
      const result = await api.postForm(`/api/campaigns/${campaignId}/leads`, formData);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);
  
  return { mutate, loading, error };
}

// ============================================================================
// BLOCK LIST (DNC) HOOKS
// ============================================================================

/**
 * Block list hook
 */
export function useBlockList(options = {}) {
  const { search = '', limit = 100, skip = 0 } = options;
  let endpoint = `/api/blocklist?limit=${limit}&skip=${skip}`;
  if (search) endpoint += `&search=${encodeURIComponent(search)}`;
  return useFetch(endpoint);
}

/**
 * Add to block list mutation hook
 */
export function useAddToBlockList() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const mutate = useCallback(async (data) => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await api.post('/api/blocklist', data);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);
  
  return { mutate, loading, error };
}

/**
 * Remove from block list mutation hook
 */
export function useRemoveFromBlockList() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const mutate = useCallback(async (id) => {
    if (!id) return null;
    
    setLoading(true);
    setError(null);
    
    try {
      const result = await api.delete(`/api/blocklist/${id}`);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);
  
  return { mutate, loading, error };
}

/**
 * Import block list mutation hook
 */
export function useImportBlockList() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const mutate = useCallback(async (formData) => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await api.postForm('/api/block-list/import', formData);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);
  
  return { mutate, loading, error };
}

// ============================================================================
// SEQUENCES HOOKS
// ============================================================================

/**
 * Sequences list hook
 */
export function useSequences(options = {}) {
  const { campaignId } = options;
  let endpoint = '/api/sequences';
  if (campaignId) endpoint += `?campaign_id=${campaignId}`;
  return useFetch(endpoint);
}

/**
 * Sequence detail hook
 */
export function useSequenceDetail(sequenceId) {
  return useFetch(sequenceId ? `/api/sequences/${sequenceId}` : null);
}

/**
 * Update sequence mutation hook
 */
export function useUpdateSequence() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const mutate = useCallback(async (sequenceId, data) => {
    if (!sequenceId) return null;
    
    setLoading(true);
    setError(null);
    
    try {
      const result = await api.patch(`/api/sequences/${sequenceId}`, data);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);
  
  return { mutate, loading, error };
}

// ============================================================================
// LEAD TIMELINE & INTEREST STATUS HOOKS
// ============================================================================

/**
 * Lead timeline (email history) hook
 */
export function useLeadTimeline(leadId) {
  return useFetch(leadId ? `/api/leads/${leadId}/timeline` : null);
}

/**
 * Lead emails (actual content) hook
 */
export function useLeadEmails(leadId) {
  return useFetch(leadId ? `/api/leads/${leadId}/emails` : null);
}

/**
 * Update lead interest status mutation hook
 */
export function useUpdateLeadInterest() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const mutate = useCallback(async (leadId, interestStatus) => {
    if (!leadId) return null;
    
    setLoading(true);
    setError(null);
    
    try {
      const result = await api.patch(`/api/leads/${leadId}/interest`, { interest_status: interestStatus });
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);
  
  return { mutate, loading, error };
}

// ============================================================================
// ENHANCED ANALYTICS HOOKS
// ============================================================================

/**
 * Per-step analytics hook
 */
export function useAnalyticsSteps(options = {}) {
  const { campaignId, days = 30 } = options;
  let endpoint = `/api/analytics/steps?days=${days}`;
  if (campaignId) endpoint += `&campaign_id=${campaignId}`;
  return useFetch(endpoint);
}

/**
 * Daily analytics breakdown hook
 */
export function useAnalyticsDaily(options = {}) {
  const { campaignId, days = 30 } = options;
  let endpoint = `/api/analytics/daily?days=${days}`;
  if (campaignId) endpoint += `&campaign_id=${campaignId}`;
  return useFetch(endpoint);
}

/**
 * Analytics pipeline (interest status funnel) hook
 */
export function useAnalyticsPipeline(options = {}) {
  const { days = 30 } = options;
  return useFetch(`/api/analytics/pipeline?days=${days}`);
}

/**
 * Analytics trends hook
 */
export function useAnalyticsTrends(options = {}) {
  const { days = 30, metric = 'all' } = options;
  return useFetch(`/api/analytics/trends?days=${days}&metric=${metric}`);
}

/**
 * Campaign step analytics hook
 */
export function useCampaignStepAnalytics(campaignId) {
  return useFetch(campaignId ? `/api/campaigns/${campaignId}/analytics/steps` : null);
}

/**
 * Campaign daily analytics hook
 */
export function useCampaignDailyAnalytics(campaignId, options = {}) {
  const { days = 30 } = options;
  return useFetch(campaignId ? `/api/campaigns/${campaignId}/analytics/daily?days=${days}` : null);
}

/**
 * Campaign sequence hook
 */
export function useCampaignSequence(campaignId) {
  return useFetch(campaignId ? `/api/campaigns/${campaignId}/sequences` : null);
}

/**
 * Campaign accounts hook
 */
export function useCampaignAccounts(campaignId) {
  return useFetch(campaignId ? `/api/campaigns/${campaignId}/accounts` : null);
}

/**
 * Campaign variants hook
 * Returns A/B test variants for a campaign step
 */
export function useCampaignVariants(campaignId, options = {}) {
  const { step = 0 } = options;
  return useFetch(campaignId ? `/api/campaigns/${campaignId}/variants?step=${step}` : null);
}

/**
 * Update campaign sequence mutation hook
 */
export function useUpdateCampaignSequence() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const mutate = useCallback(async (campaignId, data) => {
    if (!campaignId) return null;
    
    setLoading(true);
    setError(null);
    
    try {
      const result = await api.patch(`/api/campaigns/${campaignId}/sequence`, data);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);
  
  return { mutate, loading, error };
}

/**
 * Update campaign accounts mutation hook
 */
export function useUpdateCampaignAccounts() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const mutate = useCallback(async (campaignId, accounts) => {
    if (!campaignId) return null;
    
    setLoading(true);
    setError(null);
    
    try {
      const result = await api.patch(`/api/campaigns/${campaignId}/accounts`, { accounts });
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);
  
  return { mutate, loading, error };
}

// ============================================================================
// DELIVERABILITY HOOKS (Instantly Parity)
// ============================================================================

/**
 * Deliverability overview hook - aggregated health across all domains
 */
export function useDeliverabilityOverview() {
  return useFetch('/api/deliverability/overview');
}

/**
 * Domain deliverability hook - SPF/DKIM/DMARC status per domain
 */
export function useDomainDeliverability(domain) {
  return useFetch(domain ? `/api/deliverability/domain/${encodeURIComponent(domain)}` : null);
}

/**
 * All domains deliverability hook
 */
export function useDomainsDeliverability() {
  return useFetch('/api/deliverability/domains');
}

/**
 * Blacklist check hook - check if domains/IPs are blacklisted
 */
export function useBlacklistCheck(options = {}) {
  const { domain, ip } = options;
  let endpoint = '/api/deliverability/blacklist';
  const params = [];
  if (domain) params.push(`domain=${encodeURIComponent(domain)}`);
  if (ip) params.push(`ip=${encodeURIComponent(ip)}`);
  if (params.length > 0) endpoint += `?${params.join('&')}`;
  return useFetch(endpoint);
}

/**
 * Warmup status overview hook - all accounts warmup progress
 */
export function useWarmupOverview() {
  return useFetch('/api/warmup/overview');
}

/**
 * Individual account warmup status hook
 */
export function useAccountWarmupStatus(email) {
  return useFetch(email ? `/api/warmup/status/${encodeURIComponent(email)}` : null);
}

/**
 * Warmup settings hook
 */
export function useWarmupSettings(email) {
  return useFetch(email ? `/api/warmup/settings/${encodeURIComponent(email)}` : null);
}

/**
 * Update warmup settings mutation hook
 */
export function useUpdateWarmupSettings() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const mutate = useCallback(async (email, settings) => {
    if (!email) return null;
    
    setLoading(true);
    setError(null);
    
    try {
      const result = await api.patch(`/api/warmup/settings/${encodeURIComponent(email)}`, settings);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);
  
  return { mutate, loading, error };
}

/**
 * Bounce rate analytics hook
 */
export function useBounceAnalytics(options = {}) {
  const { days = 30 } = options;
  return useFetch(`/api/deliverability/bounces?days=${days}`);
}

/**
 * Spam complaint analytics hook
 */
export function useSpamAnalytics(options = {}) {
  const { days = 30 } = options;
  return useFetch(`/api/deliverability/spam?days=${days}`);
}

// ============================================================================
// EMAIL ACCOUNTS HOOKS (Instantly Parity)
// ============================================================================

/**
 * All email accounts hook - sending accounts with status
 */
export function useEmailAccounts(options = {}) {
  const { limit = 500 } = options;
  let endpoint = `/api/accounts?limit=${limit}`;
  return useFetch(endpoint);
}

/**
 * Single email account details hook
 */
export function useEmailAccount(email) {
  return useFetch(email ? `/api/accounts/${encodeURIComponent(email)}` : null);
}

/**
 * Email account analytics hook - per-account performance
 */
export function useEmailAccountAnalytics(email, options = {}) {
  const { days = 30 } = options;
  return useFetch(email ? `/api/accounts/${encodeURIComponent(email)}/analytics?days=${days}` : null);
}

/**
 * Update email account mutation hook
 */
export function useUpdateEmailAccount() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const mutate = useCallback(async (email, data) => {
    if (!email) return null;
    
    setLoading(true);
    setError(null);
    
    try {
      const result = await api.patch(`/api/accounts/${encodeURIComponent(email)}`, data);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);
  
  return { mutate, loading, error };
}

/**
 * Toggle warmup for email account
 */
export function useToggleWarmup() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const mutate = useCallback(async (email, enabled) => {
    if (!email) return null;
    
    setLoading(true);
    setError(null);
    
    try {
      const result = await api.post(`/api/accounts/${encodeURIComponent(email)}/warmup`, { enabled });
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);
  
  return { mutate, loading, error };
}

/**
 * Update daily sending limit for an email account
 */
export function useUpdateAccountDailyLimit() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const mutate = useCallback(async (email, dailyLimit) => {
    if (!email) return null;
    
    setLoading(true);
    setError(null);
    
    try {
      const result = await api.patch(`/api/accounts/${encodeURIComponent(email)}`, { 
        daily_limit: dailyLimit,
        warmup_daily_limit: dailyLimit 
      });
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);
  
  return { mutate, loading, error };
}

/**
 * Run DNS check for a domain
 */
export function useRunDNSCheck() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const mutate = useCallback(async (domain) => {
    if (!domain) return null;
    
    setLoading(true);
    setError(null);
    
    try {
      const result = await api.post(`/api/deliverability/dns-check`, { domain });
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);
  
  return { mutate, loading, error };
}

/**
 * Run blacklist check (MXToolbox)
 */
export function useRunBlacklistCheck() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const mutate = useCallback(async (domain) => {
    if (!domain) return null;
    
    setLoading(true);
    setError(null);
    
    try {
      const result = await api.post(`/api/deliverability/blacklist-check`, { domain });
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);
  
  return { mutate, loading, error };
}

// ============================================================================
// AUDIT LOG HOOKS (Instantly Parity)
// ============================================================================

/**
 * Audit log hook - system activity history
 */
export function useAuditLog(options = {}) {
  const { action, actor, entity_type, limit = 100, offset = 0 } = options;
  let endpoint = `/api/audit-log?limit=${limit}&offset=${offset}`;
  if (action) endpoint += `&action=${action}`;
  if (actor) endpoint += `&actor=${encodeURIComponent(actor)}`;
  if (entity_type) endpoint += `&entity_type=${entity_type}`;
  return useFetch(endpoint);
}

/**
 * Audit log for specific entity
 */
export function useEntityAuditLog(entityType, entityId) {
  return useFetch(entityType && entityId 
    ? `/api/audit-log/${entityType}/${entityId}` 
    : null
  );
}

// ============================================================================
// GLOBAL ANALYTICS HOOKS (Instantly Parity - Phase C)
// ============================================================================

/**
 * Workspace-level aggregate analytics
 */
export function useWorkspaceAnalytics(options = {}) {
  const { days = 30 } = options;
  // Uses existing /api/analytics/overview endpoint (POST-LAUNCH: implement full /api/analytics/workspace)
  return useFetch(`/api/analytics/overview?days=${days}`);
}

/**
 * Campaign performance comparison
 */
export function useCampaignComparison(options = {}) {
  const { days = 30, sortBy = 'reply_rate', order = 'desc' } = options;
  return useFetch(`/api/analytics/campaigns?days=${days}&sort_by=${sortBy}&order=${order}`);
}

/**
 * Email account performance comparison
 */
export function useEmailAccountComparison(options = {}) {
  const { days = 30, sortBy = 'sent', order = 'desc' } = options;
  return useFetch(`/api/analytics/accounts?days=${days}&sort_by=${sortBy}&order=${order}`);
}

/**
 * Trend analytics (time-series data)
 */
export function useTrendAnalytics(options = {}) {
  const { days = 30, metrics = ['sent', 'opens', 'clicks', 'replies'] } = options;
  return useFetch(`/api/analytics/trends?days=${days}&metrics=${metrics.join(',')}`);
}

/**
 * Time period comparison (current vs previous)
 */
export function useTimePeriodComparison(options = {}) {
  const { days = 30 } = options;
  return useFetch(`/api/analytics/period-comparison?days=${days}`);
}

// ============================================================================
// ADVANCED CAMPAIGN ANALYTICS HOOKS (Instantly Parity - Phase D)
// ============================================================================

/**
 * Campaign time-of-day analytics hook
 * Returns hourly/daily performance data and 7x24 heatmap matrix
 */
export function useCampaignTimeAnalytics(campaignId, options = {}) {
  const { days = 30 } = options;
  return useFetch(campaignId ? `/api/campaigns/${campaignId}/analytics/time?days=${days}` : null);
}

/**
 * Campaign subject line analytics hook
 * Returns performance breakdown by subject line
 */
export function useCampaignSubjectAnalytics(campaignId, options = {}) {
  const { days = 30, limit = 50 } = options;
  return useFetch(campaignId ? `/api/campaigns/${campaignId}/analytics/subjects?days=${days}&limit=${limit}` : null);
}

/**
 * Campaign A/B test results hook
 * Returns variant performance comparison data
 */
export function useCampaignABTestResults(campaignId, options = {}) {
  const { days = 30 } = options;
  return useFetch(campaignId ? `/api/campaigns/${campaignId}/analytics/ab-tests?days=${days}` : null);
}

/**
 * Campaign sequence funnel hook
 * Returns step-by-step funnel conversion data
 */
export function useCampaignSequenceFunnel(campaignId, options = {}) {
  const { days = 30 } = options;
  return useFetch(campaignId ? `/api/campaigns/${campaignId}/analytics/funnel?days=${days}` : null);
}

/**
 * Campaign leads hook
 * Returns leads for a specific campaign
 */
export function useCampaignLeads(campaignId, options = {}) {
  const { page = 1, limit = 50, status } = options;
  let url = `/api/campaigns/${campaignId}/leads?page=${page}&limit=${limit}`;
  if (status) url += `&status=${status}`;
  return useFetch(campaignId ? url : null);
}

/**
 * Available leads for campaign assignment
 * Returns leads from the client NOT already in this campaign
 */
export function useAvailableLeadsForCampaign(campaignId, search = '') {
  let url = `/api/campaigns/${campaignId}/available-leads`;
  if (search) url += `?search=${encodeURIComponent(search)}`;
  return useFetch(campaignId ? url : null);
}

/**
 * Assign existing leads to a campaign
 */
export function useAssignLeadsToCampaign() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const mutate = useCallback(async (campaignId, leadIds) => {
    if (!campaignId) throw new Error('Campaign ID is required');
    if (!leadIds?.length) throw new Error('At least one lead ID is required');
    
    setLoading(true);
    setError(null);
    
    try {
      const result = await api.post(`/api/campaigns/${campaignId}/assign-leads`, {
        lead_ids: leadIds,
      });
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);
  
  return { mutate, loading, error };
}

/**
 * Campaign bounces hook
 * Returns bounce analytics for a campaign
 */
export function useCampaignBounces(campaignId, options = {}) {
  const { days = 30 } = options;
  return useFetch(campaignId ? `/api/campaigns/${campaignId}/analytics/bounces?days=${days}` : null);
}

/**
 * Campaign engagement hook
 * Returns engagement metrics (opens, clicks, etc.)
 */
export function useCampaignEngagement(campaignId, options = {}) {
  const { days = 30 } = options;
  return useFetch(campaignId ? `/api/campaigns/${campaignId}/analytics/engagement?days=${days}` : null);
}

/**
 * Campaign timing hook
 * Returns send time performance data
 */
export function useCampaignTiming(campaignId, options = {}) {
  const { days = 30 } = options;
  return useFetch(campaignId ? `/api/campaigns/${campaignId}/analytics/time?days=${days}` : null);
}

/**
 * Campaign geography hook
 * Returns geographic distribution of engagement
 */
export function useCampaignGeography(campaignId, options = {}) {
  const { days = 30 } = options;
  return useFetch(campaignId ? `/api/campaigns/${campaignId}/analytics/geography?days=${days}` : null);
}

/**
 * Campaign sentiment hook
 * Returns sentiment analysis of replies
 */
export function useCampaignSentiment(campaignId, options = {}) {
  const { days = 30 } = options;
  return useFetch(campaignId ? `/api/campaigns/${campaignId}/analytics/sentiment?days=${days}` : null);
}

// ============================================================================
// LIVE ACTIVITY HOOKS
// ============================================================================

/**
 * Live stats hook
 * Returns real-time statistics with auto-refresh
 */
export function useLiveStats(options = {}) {
  const { interval = 5000 } = options;
  return useFetch('/api/stats/live', { refreshInterval: interval });
}

/**
 * Activity feed hook
 * Returns recent activity events with filters
 */
export function useActivityFeed(options = {}) {
  const { limit = 50, type, campaignId, clientId, since } = options;
  let url = `/api/activity?limit=${limit}`;
  if (type) url += `&type=${type}`;
  if (campaignId) url += `&campaign_id=${campaignId}`;
  if (clientId) url += `&client_id=${clientId}`;
  if (since) url += `&since=${since}`;
  return useFetch(url, { refreshInterval: 5000 });
}

/**
 * Live connection hook
 * Manages WebSocket/SSE connection for real-time updates
 */
export function useLiveConnection(options = {}) {
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState(null);
  
  // For now, simulate connection state based on fetch availability
  useEffect(() => {
    setConnected(true);
    return () => setConnected(false);
  }, []);
  
  return { connected, error, reconnect: () => setConnected(true) };
}

// ============================================================================
// REPORTS HOOKS
// ============================================================================

/**
 * Report templates hook
 * Returns available report templates
 */
export function useReportTemplates() {
  return useFetch('/api/reports/templates');
}

/**
 * Scheduled reports hook
 * Returns list of scheduled reports
 */
export function useScheduledReports() {
  return useFetch('/api/reports/scheduled');
}

/**
 * Generate report mutation hook
 */
export function useGenerateReport() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const mutate = useCallback(async (reportConfig) => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await api.post('/api/reports/generate', reportConfig);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);
  
  return { mutate, loading, error };
}

/**
 * Create scheduled report mutation hook
 */
export function useCreateScheduledReport() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const mutate = useCallback(async (scheduleConfig) => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await api.post('/api/reports/scheduled', scheduleConfig);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);
  
  return { mutate, loading, error };
}

/**
 * Delete scheduled report mutation hook
 */
export function useDeleteScheduledReport() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const mutate = useCallback(async (reportId) => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await api.delete(`/api/reports/scheduled/${reportId}`);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);
  
  return { mutate, loading, error };
}

/**
 * Client leaderboard hook
 * Returns ranked list of clients by performance metrics
 */
export function useClientLeaderboard(options = {}) {
  const { days = 30, metric = 'replies', limit = 10 } = options;
  return useFetch(`/api/analytics/leaderboard?days=${days}&metric=${metric}&limit=${limit}`);
}

/**
 * Needs Attention / Notification Center hook
 * Returns aggregated attention items from all system areas
 */
export function useNeedsAttention() {
  return useFetch('/api/notifications/attention', { refreshInterval: 60000 }); // Refresh every 60s
}

// ============================================================================
// NICHE BRIEFS
// ============================================================================

export function useNicheBriefs() {
  return useFetch('/api/niche-briefs');
}

export function useNicheBrief(nicheId) {
  return useFetch(nicheId && nicheId !== 'new' ? `/api/niche-briefs/${nicheId}` : null);
}

export function useNicheTemplates(nicheId) {
  return useFetch(nicheId ? `/api/niche-briefs/${nicheId}/templates` : null);
}

export function useDiscoveredLeads(nicheId, { page = 1, perPage = 50, status = null, search = null } = {}) {
  const params = new URLSearchParams({ page: String(page), per_page: String(perPage) });
  if (status) params.set('status', status);
  if (search) params.set('search', search);
  return useFetch(nicheId && nicheId !== 'new' ? `/api/niche-briefs/${nicheId}/discovered-leads?${params}` : null);
}

// ============================================================================
// OUTBOUND PIPELINE
// ============================================================================

export function usePipelineStats(nicheId) {
  const endpoint = nicheId ? `/api/outbound-pipeline/stats?niche_id=${nicheId}` : '/api/outbound-pipeline/stats';
  return useFetch(endpoint);
}

export function useNichePerformance() {
  return useFetch('/api/outbound-pipeline/niche-performance');
}

